/**
 * Local-dev sign-in: an HS256 access token shaped like the server's `devtoken`
 * (sub = user uuid, email, aud "authenticated", iss "better-lights-dev"), signed in the browser
 * with AUTH_DEV_SECRET. Only compiled into builds with VITE_DEV_AUTH=1; the production server
 * rejects HS256 tokens because it has no dev secret.
 */
const enc = new TextEncoder();

export function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const b64json = (v: unknown) => base64url(enc.encode(JSON.stringify(v)));

/** Server's dev namespace (server/internal/auth DevUserID): devtoken, dev-grant and web agree on ids. */
const DEV_NAMESPACE = '6f0e7c1e-5b7a-4c39-9a51-2d0f6a1c9b11';

function uuidBytes(u: string): Uint8Array {
  const hex = u.replace(/-/g, '');
  return Uint8Array.from({ length: 16 }, (_, i) => Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16));
}

/** Stable user id for an email: UUIDv5(DEV_NAMESPACE, lowercased trimmed email), like the server. */
export async function devUserId(email: string): Promise<string> {
  const name = enc.encode(email.trim().toLowerCase());
  const data = new Uint8Array(16 + name.length);
  data.set(uuidBytes(DEV_NAMESPACE));
  data.set(name, 16);
  const h = new Uint8Array(await crypto.subtle.digest('SHA-1', data)).slice(0, 16);
  h[6] = (h[6] & 0x0f) | 0x50; // version 5
  h[8] = (h[8] & 0x3f) | 0x80; // RFC 4122 variant
  const x = Array.from(h, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`;
}

export async function mintDevToken(
  email: string,
  secret: string,
  ttlSeconds = 24 * 3600,
  now = Date.now(),
): Promise<{ token: string; userId: string; expiresAt: number }> {
  const userId = await devUserId(email);
  const iat = Math.floor(now / 1000);
  const header = b64json({ alg: 'HS256', typ: 'JWT' });
  const payload = b64json({
    email: email.trim().toLowerCase(),
    sub: userId,
    aud: ['authenticated'],
    iss: 'better-lights-dev',
    iat,
    exp: iat + ttlSeconds,
  });
  const key = await crypto.subtle.importKey('raw', enc.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(`${header}.${payload}`)));
  return { token: `${header}.${payload}.${base64url(sig)}`, userId, expiresAt: (iat + ttlSeconds) * 1000 };
}
